import Collapse from '@elementor/ui/Collapse';
import Divider from '@elementor/ui/Divider';
import PropTypes from 'prop-types';
import { memo } from 'react';
import infoTipThumbnail from '@assets/image/thumb-heading-structure.png';
import infoTipVideoSrc from '@assets/video/heading-structure.webm';
import FixButton from '@components/fix-button';
import GetFreeInfoTip from '@components/get-free-info-tip';
import { EA11Y_RULES } from '@rules';
import {
	StyledListItemBottomWrapper,
	StyledListItemDetails,
	StyledTreeListItem,
	StyledListItemActionsWrapper,
} from '@styles/heading-structure.styles';
import { HEADING_STATUS } from '@types/heading';
import { __ } from '@utils/i18n';
import HeadingTreeListItemAlert from './alert';
import HeadingTreeListItemLevel from './level';
import HeadingTreeListItemTopWrapper from './top-wrapper';

const HeadingStructureHeadingTreeListItemBase = ({
	id,
	level,
	content,
	status,
	violation = null,
	isExpanded,
	toggleHeading,
}) => {
	const displayLevel = `h${level}`;

	return (
		<StyledTreeListItem isExpanded={isExpanded}>
			<HeadingTreeListItemTopWrapper
				displayLevel={displayLevel}
				id={id}
				level={level}
				status={status}
				content={content}
				isExpanded={isExpanded}
				toggleHeading={toggleHeading}
			/>

			<Collapse
				in={isExpanded}
				timeout={190}
				unmountOnExit
				mountOnEnter
				appear={false}
				sx={{ width: '100%' }}
			>
				<StyledListItemDetails role="region" aria-labelledby={id}>
					<Divider />

					<StyledListItemBottomWrapper>
						<HeadingTreeListItemLevel
							displayLevel={displayLevel}
							status={status}
						/>

						{[HEADING_STATUS.ERROR, HEADING_STATUS.WARNING].includes(status) &&
							violation && (
								<HeadingTreeListItemAlert
									status={status}
									violation={violation}
								/>
							)}
					</StyledListItemBottomWrapper>

					<StyledListItemActionsWrapper>
						{HEADING_STATUS.SUCCESS !== status && (
							<FixButton
								infoTipContent={
									<GetFreeInfoTip
										videoSrc={infoTipVideoSrc}
										posterSrc={infoTipThumbnail}
										title={__('Manage your heading structure with Ally')}
										description={__(
											'View all your headings at a glance, reorder levels, and apply a clean, consistent structure instantly.',
										)}
									/>
								}
							/>
						)}
					</StyledListItemActionsWrapper>
				</StyledListItemDetails>
			</Collapse>
		</StyledTreeListItem>
	);
};

const HeadingStructureHeadingTreeListItem = memo(
	HeadingStructureHeadingTreeListItemBase,
	(prev, next) =>
		prev.id === next.id &&
		prev.level === next.level &&
		prev.content === next.content &&
		prev.status === next.status &&
		prev.violation === next.violation &&
		prev.isExpanded === next.isExpanded,
);

const propTypes = {
	id: PropTypes.string.isRequired,
	level: PropTypes.number.isRequired,
	content: PropTypes.string.isRequired,
	status: PropTypes.oneOf(Object.values(HEADING_STATUS)).isRequired,
	violation: PropTypes.oneOf(Object.values(EA11Y_RULES)),
	isExpanded: PropTypes.bool.isRequired,
	toggleHeading: PropTypes.func.isRequired,
};

HeadingStructureHeadingTreeListItemBase.propTypes = propTypes;
HeadingStructureHeadingTreeListItem.propTypes = propTypes;

export default HeadingStructureHeadingTreeListItem;
